import React from "react";
import { format, parse, isValid } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Shadcn-based date picker. Stores value as "YYYY-MM-DD" string for backend
 * compatibility but renders the visual day picker. Drop-in replacement for
 * <input type="date" />.
 */
export default function DatePicker({ value, onChange, placeholder = "Pick a date", testId, className, disabled, ...rest }) {
  const parsed = value ? parse(value, "yyyy-MM-dd", new Date()) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-normal bg-white/5 border-white/10 text-slate-200 hover:bg-white/10 hover:text-amber-300 rounded-xl",
            !selected && "text-slate-400",
            className
          )}
          {...rest}
        >
          <CalendarIcon className="mr-2 h-4 w-4 opacity-70" />
          {selected ? format(selected, "PPP") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-slate-950 border-white/10" align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || new Date(1995, 0)}
          captionLayout="dropdown-buttons"
          fromYear={1900}
          toYear={new Date().getFullYear() + 2}
          onSelect={(d) => {
            if (d) onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          initialFocus
          className="text-slate-200"
        />
      </PopoverContent>
    </Popover>
  );
}
